import React, { type ReactNode, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { TotpMultiFactorGenerator, multiFactor } from 'firebase/auth';
import { AuthContext } from '../../contexts/auth';
import { auth } from '../../api/firebase/config';
import { getMfaRequirement } from '../../api/firebase/function/security/securityMfaFunction';
import { isOperatorStaff } from '../../utils/permission';
import { MFA_REQUIREMENT_RECHECK_MIN_INTERVAL_MS } from '../../constants/enum';
import { isSecurityGatePublicPath } from '../../utils/securityGatePublicPath';
import MfaSetupRequired from '../pages/common/MfaSetupRequired';

/** ログイン中ユーザーが認証アプリ（TOTP）を登録済みか */
const hasTotpFactor = (): boolean => {
  const user = auth.currentUser;
  if (!user) return false;
  return multiFactor(user).enrolledFactors.some(
    factor => factor.factorId === TotpMultiFactorGenerator.FACTOR_ID,
  );
};

/**
 * MFA テナント強制ゲート（#2984・PRD §5-3）。
 * テナントで「全メンバー MFA 必須」が有効な場合、認証アプリ未設定のメンバーには
 * 通常画面の代わりに設定要求画面（MfaSetupRequired）を表示する。
 *
 * - 判定はサーバー側（getMfaRequirement callable）で行い、ログインユーザーが変わった
 *   タイミングで実行する（companyMember は Firestore ルール上 config を直接読めないため）
 * - 管理者が必須化した際にログイン中のメンバーへも速やかに反映されるよう、
 *   タブ復帰（focus / visibilitychange）時にも下限間隔付きで再判定する
 *   （サーバー側では必須化時に未登録メンバーのリフレッシュトークンを失効させており、
 *     遅くとも ID トークン失効のタイミングで強制ログアウトされる）
 * - 運営スタッフ（admin / operator）は対象外（代理ログインを止めない）
 * - 判定はノンブロッキング（結果待ちで初期表示を遅らせない）。判定不能時は表示を許可する
 */
const MfaEnforcementGate = ({ children }: { children: ReactNode }): JSX.Element => {
  const { user } = useContext(AuthContext);
  const { pathname } = useLocation();
  const [required, setRequired] = useState(false);
  const [enrolled, setEnrolled] = useState(true);

  const uid = user?.uid ?? null;
  const isOperator = isOperatorStaff(user?.role);

  useEffect(() => {
    if (!uid || isOperator) {
      setRequired(false);
      return undefined;
    }
    let cancelled = false;
    let lastCheckedAt = 0;

    const check = (isInitial: boolean): void => {
      lastCheckedAt = Date.now();
      setEnrolled(hasTotpFactor());
      getMfaRequirement()
        .then(result => {
          if (!cancelled) setRequired(result.mfa_required);
        })
        .catch(() => {
          // 初回の判定不能時は表示を許可（設定変更時のロックアウトを避ける）。
          // 再判定時の一時的なエラーでは直前の判定結果を維持する
          if (!cancelled && isInitial) setRequired(false);
        });
    };
    check(true);

    // タブ復帰時の再判定（下限間隔でスロットリング）
    const recheck = (): void => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastCheckedAt < MFA_REQUIREMENT_RECHECK_MIN_INTERVAL_MS) return;
      check(false);
    };
    window.addEventListener('focus', recheck);
    document.addEventListener('visibilitychange', recheck);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', recheck);
      document.removeEventListener('visibilitychange', recheck);
    };
  }, [uid, isOperator]);

  if (uid && !isOperator && required && !enrolled && !isSecurityGatePublicPath(pathname)) {
    return <MfaSetupRequired onEnrolled={(): void => setEnrolled(true)} />;
  }

  return <>{children}</>;
};

export default MfaEnforcementGate;
